import type { StateCreator } from 'zustand';
import type { GoldenTriangle, TriangleConfig, TriangleSnapshot } from '../../types/triangle';
import { DEFAULT_TRIANGLE_CONFIG } from '../../types/triangle';
import { calculateGoldenTriangle } from '../../utils/triangleCalculator';

// ============================================================================
// GOLDEN TRIANGLE SLICE
// ============================================================================

export interface TriangleSlice {
  // State
  goldenTriangle: GoldenTriangle | null;
  triangleConfig: TriangleConfig;
  triangleHistory: TriangleSnapshot[];

  // Actions
  calculateTriangle: () => void;
  updateTriangleConfig: (config: Partial<TriangleConfig>) => void;
  resetTriangleConfig: () => void;
  clearTriangleHistory: () => void;
  toggleAutoUpdate: () => void;
}

export const createTriangleSlice: StateCreator<
  any,
  [],
  [],
  TriangleSlice
> = (set, get) => {
  const canEdit = (): boolean => {
    const state = get();
    const role = state.currentProject?.currentUserRole;
    if (role) {
      const allowed = role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR';
      if (!allowed) {
        state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      }
      return allowed;
    }
    if (state.storageMode === 'backend') {
      state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      return false;
    }
    return true;
  };

  return ({
  // Initial state
  goldenTriangle: null,
  triangleConfig: DEFAULT_TRIANGLE_CONFIG,
  triangleHistory: [],

  // Calculate the golden triangle
  calculateTriangle: () => {
    if (!canEdit()) return;
    const { nodes, edges, currentProject, triangleConfig } = get();

    if (!currentProject) {
      console.warn('No current project to calculate triangle');
      return;
    }

    if (nodes.length === 0) {
      console.warn('No nodes to calculate triangle');
      return;
    }

    try {
      const triangle = calculateGoldenTriangle(nodes, edges, currentProject, triangleConfig);

      // Create snapshot for history
      const snapshot: TriangleSnapshot = {
        id: `snapshot-${Date.now()}`,
        projectId: currentProject.id,
        timestamp: new Date(),
        cost: triangle.cost.value,
        time: triangle.time.value,
        quality: triangle.quality.value,
        balance: triangle.balance,
        overallHealth: triangle.overallHealth,
      };

      set((state: any) => ({
        goldenTriangle: triangle,
        triangleHistory: [...state.triangleHistory, snapshot].slice(-50), // Keep last 50 snapshots
      }));

      // Notify user
      get().addNotification(
        'success',
        `Triangle d'Or calculé - Santé globale: ${triangle.overallHealth.toFixed(1)}%`
      );
    } catch (error) {
      console.error('Error calculating triangle:', error);
      get().addNotification(
        'error',
        `Erreur lors du calcul du Triangle d'Or: ${error instanceof Error ? error.message : 'erreur inconnue'}`
      );
    }
  },

  // Update triangle configuration
  updateTriangleConfig: (config) => {
    if (!canEdit()) return;
    set((state: any) => ({
      triangleConfig: {
        ...state.triangleConfig,
        ...config,
      },
    }));

    // Recalculate if auto-update is enabled
    const { goldenTriangle } = get();
    if (goldenTriangle?.autoUpdate) {
      get().calculateTriangle();
    }
  },

  // Reset configuration to defaults
  resetTriangleConfig: () => {
    if (!canEdit()) return;
    set({ triangleConfig: DEFAULT_TRIANGLE_CONFIG });
    get().addNotification('info', 'Configuration du Triangle d\'Or réinitialisée');
  },

  // Clear triangle history
  clearTriangleHistory: () => {
    if (!canEdit()) return;
    set({ triangleHistory: [] });
    get().addNotification('info', 'Historique du Triangle d\'Or effacé');
  },

  // Toggle auto-update
  toggleAutoUpdate: () => {
    if (!canEdit()) return;
    set((state: any) => ({
      goldenTriangle: state.goldenTriangle
        ? {
            ...state.goldenTriangle,
            autoUpdate: !state.goldenTriangle.autoUpdate,
          }
        : null,
    }));

    const { goldenTriangle } = get();
    if (goldenTriangle) {
      get().addNotification(
        'info',
        `Mise à jour automatique ${goldenTriangle.autoUpdate ? 'activée' : 'désactivée'}`
      );
    }
  },
  });
};
